import React, { useMemo, useState } from 'react';
import {PieChartOutlined, LogoutOutlined, DatabaseOutlined, BankOutlined, SwitcherOutlined, LineChartOutlined, FileSearchOutlined, ScheduleOutlined, UserOutlined, CloudUploadOutlined} from '@ant-design/icons';
import { Menu, Layout } from 'antd';
import { Link } from 'react-router-dom';
import { FaRegHandshake } from 'react-icons/fa';
import { HiOutlinePuzzlePiece } from 'react-icons/hi2';
import { BiSolidComponent } from 'react-icons/bi';
import { useDispatch } from 'react-redux';

import { logoutUser } from '../../stateManagement/actions/auth';
import { changePublisherView } from '../../stateManagement/actions/report';
import { useAuth } from '../../hooks/useAuth';
import { ADMIN_ACCESS } from '../../utils/constants';

import { canAccessRoute } from '../../utils/routeAccess';

const { Sider } = Layout;

const menuItem = (key, icon, label, path) => ({
  key,
  icon,
  label: <Link to={path}>{label}</Link>,
});

const getDefaultMenuItems = ({ isThd = false, isAdmin = false } = {}) => [
  menuItem('dashboard', <PieChartOutlined />, 'Dashboard', isThd ? '/thd' : '/'),
  !isThd || isAdmin ? menuItem('deals', <FaRegHandshake />, 'Deals', isThd ? '/thd/deals' : '/deals') : null,
  isThd && menuItem('direct-thd', <BankOutlined />, 'Direct', '/thd/direct'),
  !isThd || isAdmin ? menuItem('trends', <LineChartOutlined />, 'Trends', isThd ? '/thd/trends' : '/trends') : null,
  !isThd || isAdmin ? menuItem('reports', <FileSearchOutlined />, 'Reports', isThd ? '/thd/reports' : '/reports') : null,
  !isThd || isAdmin ? menuItem('data-availability', <DatabaseOutlined />, 'Data availability', '/data-availability') : null,
];

const getDevMenuItems = () => [
  menuItem('dashboard', <PieChartOutlined />, 'Dashboard', '/'),
  menuItem('dashboard-thd', <PieChartOutlined />, 'Dashboard THD', '/thd'),
  menuItem('deals', <FaRegHandshake />, 'Deals', '/deals'),
  menuItem('deals-thd', <FaRegHandshake />, 'Deals THD', '/thd/deals'),
  menuItem('direct-thd', <BankOutlined />, 'Direct THD', '/thd/direct'),
  menuItem('trends', <LineChartOutlined />, 'Trends', '/trends'),
  menuItem('trends-thd', <LineChartOutlined />, 'Trends THD', '/thd/trends'),
  menuItem('reports', <FileSearchOutlined />, 'Reports', '/reports'),
  menuItem('reports-thd', <FileSearchOutlined />, 'Reports THD', '/thd/reports'),
  menuItem('data-availability', <DatabaseOutlined />, 'Data availability', '/data-availability'),
  menuItem('mapping-thd', <HiOutlinePuzzlePiece />, 'Mapping THD', '/thd/mapping'),
];

const getAdminMenuItems = () => [
  menuItem('mapping', <HiOutlinePuzzlePiece />, 'Mapping', '/mapping'),
  menuItem('crons', <ScheduleOutlined />, 'Crons execution', '/crons'),
  menuItem('resources', <BiSolidComponent />, 'Resources', '/resources'),
  menuItem('data-upload', <CloudUploadOutlined />, 'Data upload', '/data-upload'),
  menuItem('users', <UserOutlined />, 'Users', '/users'),
];

const createMenuConfig = ({ dispatch, user }) => {
  if (!user) return [];

  const isMainAdmin = user.access === ADMIN_ACCESS && user.subAccount === false;

  const menuItems = user.devMode && user.access === ADMIN_ACCESS ? getDevMenuItems() : getDefaultMenuItems({ isThd: user.subAccount, isAdmin: user.access === ADMIN_ACCESS });

  if (isMainAdmin) {
    menuItems.push(...getAdminMenuItems({ isThd: user.subAccount }));
  }

  const accessList = user.access?.split('+') || [];

  if (accessList.length > 1) {
    menuItems.push({
      key: 'switch-account',
      icon: <SwitcherOutlined />,
      label: 'Switch account',
      children: accessList.map((access) => ({
        key: access,
        label: (
          <span onClick={() => dispatch(changePublisherView(access))}>
            {access}
          </span>
        ),
      })),
    });
  }

  menuItems.push({
    key: 'logout',
    icon: <span onClick={() => dispatch(logoutUser())}><LogoutOutlined /></span>,
    label: <span onClick={() => dispatch(logoutUser())}>Logout</span>,
  });

  return menuItems.filter(item => item && (!item.label?.props?.to || canAccessRoute(user, item.label.props.to)));
};

const SideMenu = () => {
  const [collapsed, setCollapsed] = useState(true);

  const dispatch = useDispatch();
  const { isAuthenticated, user } = useAuth();

  const menuItems = useMemo(
    () => createMenuConfig({ dispatch, user }),
    [dispatch, user]
  );

  if (!isAuthenticated) return null;

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      theme="light"
      style={{
        position: 'fixed',
        minHeight: '100vh',
        zIndex: 9,
        maxWidth: '60px',
        minWidth: '60px',
        width: '60px',
      }}
    >
      <div className={collapsed ? 'menu-logo' : 'menu-logo menu-logo--expanded'} />
      <Menu
        theme="light"
        mode="inline"
        items={menuItems}
        style={{ width: '60px' }}
      />
    </Sider>
  );
};

export default SideMenu;